<template>
  <div class="z-card__body mt-3">
    <z-form-group class="row">
      <div class="col-3 text-right">
        {{ $t('core.profile.form.field.password.current') }}
      </div>
      <z-form-input class="col-9" :rules="{ required: true }">
        <i-input
          v-model="form.current_password"
          :type="current_password_params.type"
          autocomplete="nope"
        >
          <template #suffix>
            <icon
              v-if="!!form.current_password"
              :type="current_password_params.icon"
              size="20"
              @click="toggle_current_password"
            />
          </template>
        </i-input>
      </z-form-input>
    </z-form-group>

    <z-form-group class="row">
      <div class="col-3 text-right">
        {{ $t('core.profile.form.field.password.new') }}
      </div>
      <z-form-input
        class="col-9"
        :rules="{
          required: true,
          min: 6,
          max: 128,
          password: true
        }"
      >
        <i-input v-model="form.new_password" :type="new_password_params.type" autocomplete="nope">
          <template #suffix>
            <div
              :class="{
                row: !!form.new_password && new_password_params.icon === 'md-eye-off'
              }"
            >
              <span
                v-if="!!form.new_password && new_password_params.icon === 'md-eye-off'"
                class="flex copy-row mr-1"
                @click="copy"
              >
                <icon type="md-copy" size="20" style="margin-left: -4px" />
              </span>

              <icon
                v-if="!!form.new_password"
                :type="new_password_params.icon"
                size="20"
                @click="toggle_new_password"
              />
            </div>
          </template>
        </i-input>

        <popup width="200px" style="top: -75px; right: -200px; left: inherit">
          <p>{{ $t('core.reset_password.password_requirements') }}</p>
          <conditions :valid_result="valid_result" />
          <div
            :class="{ link: true, 'mt-2': true, 'link--disabled': is_generation_loading }"
            @click="generate_password"
          >
            {{ $t('core.reset_password.generate_password') }}
          </div>
        </popup>
      </z-form-input>
    </z-form-group>

    <z-form-group class="row">
      <div class="col-3 text-right">
        {{ $t('core.profile.form.field.password.new_confirm') }}
      </div>
      <z-form-input class="col-9" :rules="{ required: true, min: 6, max: 128 }">
        <i-input
          v-model="form.new_password_confirmation"
          :type="confirm_password_params.type"
          autocomplete="nope"
        >
          <template #suffix>
            <icon
              v-if="!!form.new_password_confirmation"
              :type="confirm_password_params.icon"
              size="20"
              @click="toggle_confirm_password"
            />
          </template>
        </i-input>
      </z-form-input>
    </z-form-group>

    <div class="row">
      <div class="col-12 text-right">
        <i-button type="default" size="large" :loading="is_loading" :disabled="!password_is_valid || is_loading" @click="submit" >
          {{ $t('shared.component.button.change') }}
        </i-button>
      </div>
    </div>
  </div>
</template>

<script>
  import { ResetPasswordService } from '@core/services/ResetPasswordService';
  import { UserService } from '@core/services/UserService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import Conditions from '@shared/components/reset-password-form/conditions';
  import { validations } from '@utils/password-rules';
  import IconSuffix from '@core/components/auth/reset-password/icon-suffix';
  import Vue from 'vue';

  export default {
    components: { Conditions },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      form: {
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      },
      current_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      new_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      confirm_password_params: {
        type: 'password',
        icon: 'md-eye'
      },
      is_loading: false,
      is_generation_loading: false,
      valid_result: [null, null, null, null, null]
    }),

    computed: {
      password_is_valid: function () {
        return Object.entries(this.valid_result).every(([key, results]) => !!results);
      }
    },
    watch: {
      'form.current_password': {
        handler: function () {
          if (this.form.current_password == '') {
            this.current_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }
        },
        immediate: true,
        deep: true
      },
      'form.new_password': {
        handler: function (value) {
          if (this.form.new_password == '') {
            this.new_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(value, this.form.new_password_confirmation)
                : fn(value, this.form.new_password_confirmation) || null;
            } else {
              return this.valid_result[i] === false ? fn(value) : fn(value) || null;
            }
          });
        },
        immediate: true,
        deep: true
      },
      'form.new_password_confirmation': {
        handler: function (value) {
          if (this.form.new_password_confirmation == '') {
            this.confirm_password_params = {
              type: 'password',
              icon: 'md-eye'
            };
          }

          let lastElement = validations.length - 1;
          this.valid_result = validations.map((fn, i) => {
            if (i === lastElement) {
              return this.valid_result[i] === false
                ? fn(this.form.new_password, value)
                : fn(this.form.new_password, value) || null;
            } else {
              return this.valid_result[i] === false
                ? fn(this.form.new_password)
                : fn(this.form.new_password) || null;
            }
          });
        },
        immediate: true,
        deep: true
      }
    },
    methods: {
      copy() {
        this.$copyText(this.form.new_password).then(() => {
          this.$noty.success(this.$t('shared.component.noty.text_copied'));
        });
      },

      toggle_current_password() {
        this.current_password_params = {
          type: this.current_password_params.type === 'password' ? 'text' : 'password',
          icon: this.current_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      toggle_new_password() {
        this.new_password_params = {
          type: this.new_password_params.type === 'password' ? 'text' : 'password',
          icon: this.new_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      toggle_confirm_password() {
        this.confirm_password_params = {
          type: this.confirm_password_params.type === 'password' ? 'text' : 'password',
          icon: this.confirm_password_params.icon === 'md-eye-off' ? 'md-eye' : 'md-eye-off'
        };
      },

      generate_password: function () {
        this.is_generation_loading = true;
        ResetPasswordService.generate()
          .then(({ password }) => {
            this.form.new_password = password || '';
            this.new_password_params = {
              type: (this.new_password_params.type = 'text'),
              icon: (this.new_password_params.icon = 'md-eye-off')
            };
          })
          .finally(() => (this.is_generation_loading = false));
      },

      submit: function () {
        const { current_password, new_password, new_password_confirmation } = this.form;

        if (this.password_is_valid) {
          this.is_loading = true;
          UserService.changePassword(this.$user.id, {
            current_password: current_password,
            new_password: new_password,
            new_password_confirmation: new_password_confirmation
          })
            .then(() => {
              setTimeout(() => {
                this.$router.push({ name: 'auth.login' });
              }, 2000);
              this.$noty.success(
                this.$t('core.reset_password.success_change_password') +
                  ' ' +
                  this.$t('core.reset_password.link.success_change_link_to_auth_page')
              );
              this.is_loading = false;
            })
            .catch((event) => {
              const R = Vue.$R;
              
              const new_password_error = R.pathOr(
                '',
                ['response', 'data', 'errors', 'new_password', 0, 'message'],
                event
              );

              const current_password_error = R.pathOr(
                '',
                ['response', 'data', 'errors', 'current_password', 0, 'message'],
                event
              );

              if (new_password_error) {
                this.$noty.error(new_password_error);
              }

              if (current_password_error) {
                this.$noty.error(current_password_error);
              }

              if (!new_password_error && !current_password_error) {
                this.$noty.server_error(event);
              }
            })
            .finally(() => (this.is_loading = false));
        } else if (
          current_password <= 0 ||
          new_password.length <= 0 ||
          new_password_confirmation <= 0
        ) {
          this.$noty.error(this.$t('shared.errors.input_form'));
        } else {
          this.$noty.error(this.$t('core.reset_password.error.not_valid_password'));
        }
      }
    }
  };
</script>
<style lang="scss" scoped>
  .ivu-input-suffix {
    right: 2px;
  }
</style>